import Link from "next/link";
import { FC } from "react";
import Grid from "./grid";
import { GridTileImage } from "./grid/tile";
import Price from "./price";
import AddToCartButton from "./add-to-cart-button";
import { NOT_IMAGE } from "@/lib/constants";
import { HeartIcon } from "@/components/icons/assets";

export const ProductCard: FC<{
  currency: string;
  price: string;
  imageUrl: string;
  product: {
    urlKey: string;
    name: string;
    id: string;
    type: string;
  };
  size?: string;
  bolt?: string;
  wheelCount?: string;
  showSpecs?: boolean;
}> = ({ currency, price, imageUrl, product, size, bolt, wheelCount, showSpecs = false }) => {
  return (
    <Grid.Item
      key={product.urlKey}
      className="animate-fadeIn gap-y-4 flex flex-col"
    >
      <div className="group relative overflow-hidden rounded-xl bg-background-50 p-4 md:p-6">
        <Link href={`/product-details/${product.urlKey}?type=${product.type}`}>
          <div className="h-[200px] w-full max-w-[258px] mx-auto cursor-pointer">
            <GridTileImage
              alt={product?.name || "Product image"}
              blurDataURL={NOT_IMAGE}
              className={`rounded-xl object-contain transition duration-300 ease-in-out group-hover:scale-110`}
              placeholder="blur"
              loading="lazy"
              priority={false}
              fill
              src={imageUrl ?? NOT_IMAGE}
            />
          </div>
        </Link>
        
        <div
          className={`absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center :gap-x-4 rounded-full border-[1.5px] border-white bg-white/70 px-4 py-1.5 text-xs font-semibold text-black opacity-0 shadow-2xl backdrop-blur-md duration-300 group-hover:opacity-100`}
        >
          <AddToCartButton
            productType={product.type}
            urlKey={product.urlKey}
            productId={product.id}
          />
        </div>
      </div>
      
      <div className="flex justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                className="w-4 h-4 md:w-5 md:h-5 text-warning fill-warning"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          {showSpecs && (size || bolt) && (
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-2 md:mb-3">
              {size && (
                <span className="font-plus-jakarta font-semibold text-xs md:text-sm text-neutral-900 bg-background-50 px-2.5 md:px-4 py-1 md:py-1.5 rounded-full">
                  {size}
                </span>
              )}
              {bolt && (
                <span className="font-plus-jakarta font-semibold text-xs md:text-sm text-neutral-900 bg-background-50 px-2.5 md:px-4 py-1 md:py-1.5 rounded-full">
                  {bolt}
                </span>
              )}
            </div>
          )}
          <h2 className="font-plus-jakarta font-medium text-neutral-300 text-xs md:text-sm mb-2 md:mb-3 line-clamp-2">
            {product?.name}
          </h2>
          <div className="flex flex-wrap items-center gap-1 md:gap-2">
            <Price
              amount={price}
              className="text-lg md:text-2xl font-plus-jakarta font-extrabold text-neutral-900"
              currencyCode={currency}
            />
            {showSpecs && wheelCount && (
              <span className="font-plus-jakarta font-medium text-neutral-300 text-xs md:text-base">
                {wheelCount}
              </span>
            )}
          </div>
        </div>
        <button className="w-10 h-10 bg-background-100 rounded-full cursor-pointer flex items-center justify-center border border-stroke self-start">
          <HeartIcon className="w-5 h-5" />
        </button>
      </div>
    </Grid.Item>
  );
};
